import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { clamp } from 'lodash';

// material-ui
import { Box, Drawer, IconButton, InputAdornment, Paper, Stack, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// project imports
import useAuth from 'hooks/useAuth';
import { useDispatch, useSelector } from 'store';
import {
    askQuestion,
    getAskJivanEnabled,
    resetAskJivanState,
    setOpen,
    setPanelWidth,
    setView,
    startNewConversation,
    DEFAULT_PANEL_WIDTH,
    MAX_PANEL_WIDTH,
    MIN_PANEL_WIDTH
} from 'store/slices/askJivan';
import { drawerWidth } from 'store/constant';
import { IMPERSONATION_BANNER_HEIGHT } from '../ImpersonationBanner';
import ConversationHistory from './conversationHistory';
import JivanFace from './jivanFace';
import MessageList from './messageList';

// assets
import { IconHistory, IconMessagePlus, IconSend, IconX } from '@tabler/icons';

// the fixed header the panel docks under, same figure theme.typography.mainContent and the sidebar bake in
const HEADER_HEIGHT = 88;
// the panel width lives in a CSS variable on the dock so a drag can resize it without a
// React render per pointer move; the slice value is written on release
const WIDTH_VAR = '--ask-jivan-panel-width';
// the page keeps at least this much beside the sidebar and the panel, whatever the screen
const MIN_PAGE_WIDTH = 480;
// gap theme.typography.mainContent leaves between the page and the panel
const PAGE_MARGIN_RIGHT = 20;
const MAX_QUESTION_LENGTH = 2000;

// widest the panel may be on this screen: the slice maximum, or less when the page would
// otherwise drop under MIN_PAGE_WIDTH (the sidebar is assumed open, the worse case)
export const panelWidthLimit = (viewportWidth) =>
    clamp(viewportWidth - drawerWidth - PAGE_MARGIN_RIGHT - MIN_PAGE_WIDTH, MIN_PANEL_WIDTH, MAX_PANEL_WIDTH);

const useViewportWidth = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const onResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return width;
};
// the header and composer are white lids over the tinted thread; a hairline shadow on the
// edge facing the thread marks them instead of a divider
const lidEdgeColor = (theme) => alpha(theme.palette.primary[800], 0.08);
const headerEdge = (theme) => `0 1px 0 ${lidEdgeColor(theme)}`;
const composerEdge = (theme) => `0 -1px 0 ${lidEdgeColor(theme)}`;

// Grab strip on the panel's left edge. Drags write the width straight to the dock's CSS
// variable; the slice gets the final figure on release so the layout and a later reopen agree.
const ResizeHandle = ({ dockRef, maxWidth }) => {
    const dispatch = useDispatch();
    // where the drag started, so the edge follows the pointer by the distance moved and does
    // not jump to wherever on the strip it was grabbed
    const drag = useRef(null);

    const widthFromPointer = (clientX) => clamp(Math.round(drag.current.width + drag.current.x - clientX), MIN_PANEL_WIDTH, maxWidth);

    const endDrag = () => {
        drag.current = null;
        delete dockRef.current.dataset.dragging;
        // back to the slice value; a lingering override would shadow a later reset
        dockRef.current.style.removeProperty(WIDTH_VAR);
    };

    const onPointerDown = (event) => {
        // a right click opens the context menu, which swallows the release: never start on it
        if (event.button !== 0) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = { x: event.clientX, width: dockRef.current.offsetWidth };
        dockRef.current.dataset.dragging = 'true';
    };

    const onPointerMove = (event) => {
        if (!drag.current) {
            return;
        }
        dockRef.current.style.setProperty(WIDTH_VAR, `${widthFromPointer(event.clientX)}px`);
    };

    const onPointerUp = (event) => {
        if (!drag.current) {
            return;
        }
        const width = widthFromPointer(event.clientX);
        endDrag();
        dispatch(setPanelWidth(width));
    };

    // a cancelled pointer carries no useful position: the width springs back to where it was
    const onPointerCancel = () => {
        if (drag.current) {
            endDrag();
        }
    };

    return (
        <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize Ask Jivan panel"
            data-testid="ask-jivan-resize-handle"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onDoubleClick={() => dispatch(setPanelWidth(DEFAULT_PANEL_WIDTH))}
            sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: 8,
                cursor: 'col-resize',
                touchAction: 'none',
                zIndex: 2,
                '&:hover, &:active': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15) }
            }}
        />
    );
};

ResizeHandle.propTypes = {
    dockRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    maxWidth: PropTypes.number.isRequired
};

// Header, thread (or history) and composer. The same content fills the docked panel on
// desktop and the full-screen drawer on phones.
const PanelContent = () => {
    const dispatch = useDispatch();
    const { view, conversationId, messages, pending, loadingConversation } = useSelector((state) => state.askJivan);
    const [question, setQuestion] = useState('');
    const inputRef = useRef(null);

    // the input is disabled while a question is out, which drops its focus: hand it back
    // for the follow-up, unless the user has since moved focus somewhere else on the page
    useEffect(() => {
        if (!pending && document.activeElement === document.body) {
            inputRef.current?.focus();
        }
    }, [pending]);

    const canSend = question.trim() !== '' && !pending;

    const send = async () => {
        if (!canSend) {
            return;
        }
        const asked = question.trim();
        // the question moves into the thread straight away; it comes back to the input to retry if it fails
        setQuestion('');
        const answered = await dispatch(askQuestion(conversationId, asked));
        if (!answered) {
            setQuestion(asked);
        }
    };

    const onKeyDown = (event) => {
        // Enter sends; Shift+Enter adds a line
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
        }
    };

    return (
        <Box data-testid="ask-jivan-drawer" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.5, boxShadow: headerEdge, zIndex: 1 }}>
                <JivanFace size={32} />
                <Typography variant="h4" sx={{ flex: 1 }}>
                    Ask Jivan
                </Typography>
                <Tooltip title="New conversation" placement="top">
                    {/* span keeps the tooltip working while the button is disabled */}
                    <span>
                        <IconButton
                            size="small"
                            // leaving the thread mid-question would strand the answer
                            disabled={pending}
                            onClick={() => dispatch(startNewConversation())}
                            data-testid="ask-jivan-new"
                        >
                            <IconMessagePlus size={18} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={view === 'history' ? 'Back to chat' : 'Past conversations'} placement="top">
                    <span>
                        <IconButton
                            size="small"
                            color={view === 'history' ? 'primary' : 'default'}
                            // asking in the old thread while a past one loads would strand the question
                            disabled={loadingConversation}
                            onClick={() => dispatch(setView(view === 'history' ? 'chat' : 'history'))}
                            data-testid="ask-jivan-history-toggle"
                        >
                            <IconHistory size={18} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Close" placement="top">
                    <IconButton size="small" onClick={() => dispatch(setOpen(false))} data-testid="ask-jivan-close">
                        <IconX size={18} />
                    </IconButton>
                </Tooltip>
            </Stack>
            {view === 'history' && <ConversationHistory />}
            {view === 'chat' && (
                <>
                    <MessageList messages={messages} pending={pending} />
                    <Box sx={{ p: 1.5, boxShadow: composerEdge, zIndex: 1 }}>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={5}
                            size="small"
                            placeholder="Ask a question"
                            value={question}
                            onChange={(event) => setQuestion(event.target.value)}
                            onKeyDown={onKeyDown}
                            disabled={pending}
                            inputRef={inputRef}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: (theme) => alpha(theme.palette.primary[800], 0.18)
                                }
                            }}
                            inputProps={{ maxLength: MAX_QUESTION_LENGTH, 'data-testid': 'ask-jivan-input' }}
                            // eslint-disable-next-line react/jsx-no-duplicate-props
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={!canSend}
                                            onClick={send}
                                            data-testid="ask-jivan-send"
                                            aria-label="Send"
                                        >
                                            <IconSend size={18} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

// Ask Jivan panel. Lives in MainLayout as a flex sibling of the page content, so the open
// panel and the thread survive route changes and the page shrinks to sit beside it instead
// of being covered. Launched from the top bar (Header/AskJivanSection). Hidden unless
// platform admin enabled it for the org.
const AskJivanWidget = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { organizationMeta, impersonation } = useAuth();
    const orgId = organizationMeta?.meta?.id;
    const { enabled, open, panelWidth } = useSelector((state) => state.askJivan);
    // below md the sidebar is a temporary overlay too; a docked column has no room there
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const dockRef = useRef(null);
    // a width dragged on a wide screen is capped again when the window narrows
    const maxWidth = panelWidthLimit(useViewportWidth());
    const width = Math.min(panelWidth, maxWidth);
    // the impersonation banner grows the fixed header, same as the sidebar's offset
    const headerOffset = impersonation ? HEADER_HEIGHT + IMPERSONATION_BANNER_HEIGHT : HEADER_HEIGHT;

    useEffect(() => {
        if (!orgId) {
            return;
        }
        // a thread belongs to one org: start clean when the org changes
        resetAskJivanState();
        dispatch(getAskJivanEnabled());
    }, [dispatch, orgId]);

    if (!enabled) {
        return null;
    }

    if (!matchUpMd) {
        return (
            <Drawer
                anchor="right"
                open={open}
                onClose={() => dispatch(setOpen(false))}
                PaperProps={{ sx: { width: '100%' } }}
                data-testid="ask-jivan-dock"
            >
                <PanelContent />
            </Drawer>
        );
    }

    return (
        <Box
            component="aside"
            ref={dockRef}
            data-testid="ask-jivan-dock"
            sx={{
                [WIDTH_VAR]: `${width}px`,
                flexShrink: 0,
                width: open ? `var(${WIDTH_VAR})` : 0,
                // keeps the panel in view while the page scrolls under the fixed header
                position: 'sticky',
                top: headerOffset,
                alignSelf: 'flex-start',
                height: `calc(100vh - ${headerOffset}px)`,
                overflow: 'hidden',
                // hidden only once the slide-out has finished, so the close animates
                visibility: open ? 'visible' : 'hidden',
                transition: theme.transitions.create(['width', 'visibility'], {
                    easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.shorter
                }),
                '&[data-dragging="true"]': { transition: 'none' }
            }}
        >
            <Paper
                square
                elevation={0}
                sx={{
                    position: 'relative',
                    width: `var(${WIDTH_VAR})`,
                    height: '100%',
                    borderLeft: `1px solid ${theme.palette.divider}`
                }}
            >
                <ResizeHandle dockRef={dockRef} maxWidth={maxWidth} />
                <PanelContent />
            </Paper>
        </Box>
    );
};

export default AskJivanWidget;
